import { useRef } from 'react';
import { FiActivity, FiAlertCircle, FiArchive, FiCalendar, FiClock, FiSun } from 'react-icons/fi';
import { Box, Flex } from './base';
import { Classe } from './tipos';

// A navegação entre as três visões do painel. Antes era um seletor em que a
// aba ativa virava um cartão branco e as outras ficavam sem borda: parecia
// três cartões de indicador, e quem abria não via que dava para clicar.
// Agora são pílulas: todas com borda de botão, a ativa com fundo laranja claro
// e borda laranja; setas, Home e End no teclado (padrão WAI-ARIA, com o foco
// só na aba ativa). O período não fica na pílula (está no cabeçalho da visão,
// logo abaixo, e no nome que o leitor de tela lê). Os contadores são selos
// pequenos, de número só, para não serem lidos como indicador comercial.

export type NomeDaAba = 'hoje' | 'pendencias' | 'mes_corrente' | 'mes_passado' | 'personalizado' | 'cirurgias';

export type DefinicaoDaAba = {
  nome: NomeDaAba;
  titulo: string;
  // O nome no celular, onde as três precisam caber lado a lado.
  curto: string;
  // O período da visão ("27/10 a 02/11 contra 20/10 a 26/10").
  legenda: string;
  resumo?: Record<Classe, number>;
  // A análise ainda não tem histórico: a aba abre, e explica desde quando.
  semHistorico?: boolean;
};

const ICONES: Record<NomeDaAba, React.ComponentType<{ size?: number; 'aria-hidden'?: boolean }>> = {
  hoje: FiSun,
  pendencias: FiAlertCircle,
  mes_corrente: FiCalendar,
  mes_passado: FiArchive,
  personalizado: FiCalendar,
  cirurgias: FiActivity,
};

const GRAVES: [Classe, string, string][] = [
  ['critico', 'crítico', 'críticos'],
  ['atencao', 'de atenção', 'de atenção'],
];

export const idDaAba = (nome: NomeDaAba) => `pa-aba-${nome}`;
export const idDoConteudo = (nome: NomeDaAba) => `pa-visao-${nome}`;

// "2 alertas críticos e 1 de atenção", para o leitor de tela e a dica.
export function textoDosGraves(resumo?: Record<Classe, number>): string {
  const partes = GRAVES.filter(([c]) => (resumo?.[c] || 0) > 0).map(
    ([c, um, varios]) => `${resumo![c]} ${resumo![c] === 1 ? um : varios}`,
  );
  if (!partes.length) return 'nenhum alerta crítico ou de atenção';
  return `alertas: ${partes.join(' e ')}`;
}

function Contadores({ resumo }: { resumo?: Record<Classe, number> }) {
  const graves = GRAVES.filter(([c]) => (resumo?.[c] || 0) > 0);
  if (!graves.length) return null;
  return (
    <Flex as="span" sx={{ gap: '4px' }} flexShrink={0} aria-hidden="true">
      {graves.map(([c]) => (
        <Box
          as="span"
          key={c}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          sx={{ gap: '4px' }}
          minW="22px"
          h="20px"
          px="6px"
          borderRadius="999px"
          fontSize="11px"
          fontWeight={700}
          lineHeight="20px"
          color={`var(--pa-${c})`}
          bg={`var(--pa-${c}-fundo)`}
        >
          <Box as="i" display="block" w="6px" h="6px" borderRadius="50%" bg="currentColor" />
          {resumo![c]}
        </Box>
      ))}
    </Flex>
  );
}

export function AbasDoPainel({
  abas,
  ativa,
  onEscolher,
}: {
  abas: DefinicaoDaAba[];
  ativa: NomeDaAba;
  onEscolher: (nome: NomeDaAba) => void;
}) {
  const botoes = useRef<(HTMLButtonElement | null)[]>([]);

  // Setas andam e escolhem (a troca é instantânea: o painel já tem as três).
  function teclado(e: React.KeyboardEvent, i: number) {
    const destino =
      e.key === 'ArrowRight'
        ? (i + 1) % abas.length
        : e.key === 'ArrowLeft'
          ? (i - 1 + abas.length) % abas.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? abas.length - 1
              : -1;
    if (destino < 0) return;
    e.preventDefault();
    onEscolher(abas[destino].nome);
    botoes.current[destino]?.focus();
  }

  return (
    <Flex
      role="tablist"
      aria-label="Visões do painel"
      sx={{ gap: '8px' }}
      overflowX="auto"
      // A borda de foco da pílula não é cortada pela rolagem.
      p="3px"
      m="-3px"
    >
      {abas.map((a, i) => {
        const selecionada = a.nome === ativa;
        const Icone = ICONES[a.nome];
        return (
          <Box
            as="button"
            type="button"
            key={a.nome}
            ref={(el: HTMLButtonElement | null) => (botoes.current[i] = el)}
            role="tab"
            id={idDaAba(a.nome)}
            aria-selected={selecionada}
            aria-controls={idDoConteudo(a.nome)}
            aria-label={`${a.titulo}, ${a.legenda}, ${textoDosGraves(a.resumo)}`}
            title={`${a.titulo} · ${a.legenda}`}
            tabIndex={selecionada ? 0 : -1}
            onClick={() => onEscolher(a.nome)}
            onKeyDown={(e: React.KeyboardEvent) => teclado(e, i)}
            display="inline-flex"
            alignItems="center"
            sx={{ gap: '8px' }}
            flexShrink={0}
            h={{ base: '38px', md: '42px' }}
            px={{ base: '12px', md: '18px' }}
            borderRadius="999px"
            border="1px solid"
            borderColor={selecionada ? 'var(--pa-marca)' : 'var(--pa-linha)'}
            // A ativa: fundo laranja claro e a borda dobrada na cor da marca.
            boxShadow={selecionada ? '0 0 0 1px var(--pa-marca)' : 'var(--pa-sombra)'}
            bg={selecionada ? 'var(--pa-marca-suave)' : 'var(--pa-superficie)'}
            color={selecionada ? 'var(--pa-tinta)' : 'var(--pa-tinta-2)'}
            fontSize={{ base: '13px', md: '14px' }}
            fontWeight={selecionada ? 700 : 600}
            whiteSpace="nowrap"
            cursor="pointer"
            transition="background-color .15s, color .15s, border-color .15s"
            _hover={
              selecionada
                ? undefined
                : { bg: 'var(--pa-superficie-2)', color: 'var(--pa-tinta)', borderColor: 'var(--pa-tinta-3)' }
            }
            _active={selecionada ? undefined : { bg: 'var(--pa-grade)' }}
            _focusVisible={{ outline: '2px solid var(--pa-dado)', outlineOffset: '2px' }}
          >
            <Box
              as="span"
              display={{ base: 'none', sm: 'inline-flex' }}
              color={selecionada ? 'var(--pa-marca-tinta)' : 'currentColor'}
            >
              {a.semHistorico ? <FiClock size={15} aria-hidden={true} /> : <Icone size={15} aria-hidden={true} />}
            </Box>
            <Box as="span" display={{ base: 'inline', md: 'none' }}>
              {a.curto}
            </Box>
            <Box as="span" display={{ base: 'none', md: 'inline' }}>
              {a.titulo}
            </Box>
            <Contadores resumo={a.resumo} />
          </Box>
        );
      })}
    </Flex>
  );
}
